import React, { useCallback, useEffect, useMemo, useState } from "react";
import Cropper from "react-easy-crop";
import "react-easy-crop/react-easy-crop.css";
import JSZip from "jszip";

import { api } from "@/lib/api";
import Dropzone from "@/components/Dropzone";
import HealingTool from "@/components/HealingTool";
import TopNav from "@/components/TopNav";
import TransparencyPreview from "@/components/TransparencyPreview";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { Download, Scissors, ShieldCheck, Sun, Contrast, Palette, Moon, Sparkles, Wand2 } from "lucide-react";

function useLocalStorage(key, initialValue) {
  const [value, setValue] = useState(() => {
    try {
      const raw = window.localStorage.getItem(key);
      return raw ? JSON.parse(raw) : initialValue;
    } catch {
      return initialValue;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // ignore
    }
  }, [key, value]);

  return [value, setValue];
}

function downloadBlob(url, filename) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export default function PassportStudioPage() {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const [branchCode, setBranchCode] = useLocalStorage("genx_branch_code", "");
  const [branchValid, setBranchValid] = useState(false);
  const [validating, setValidating] = useState(false);

  const [showCrop, setShowCrop] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const [zipBlob, setZipBlob] = useState(null);
  const [zipUrl, setZipUrl] = useState(null);

  // Image adjustment controls
  const [brightness, setBrightness] = useState(0);
  const [contrast, setContrast] = useState(0);
  const [tint, setTint] = useState(0);
  const [darken, setDarken] = useState(0);
  const [removeShine, setRemoveShine] = useState(true);
  const [edgeCut, setEdgeCut] = useState(18);  // Edge cut pixels (0-50)
  const [edgeSmooth, setEdgeSmooth] = useState(0);  // Edge smooth pixels (0-20)

  const [size, setSize] = useState("us_2x2");
  const [restore, setRestore] = useState(false);  // Disabled by default

  const aspect = useMemo(() => {
    if (size === "ca_35x45") return 35 / 45;
    return 1;
  }, [size]);

  const [passportUrl, setPassportUrl] = useState(null);
  const [sheetUrl, setSheetUrl] = useState(null);

  const [useCrop, setUseCrop] = useState(true);
  
  // Track cropped preview image
  const [croppedPreviewUrl, setCroppedPreviewUrl] = useState(null);
  const [originalFileUrl, setOriginalFileUrl] = useState(null);
  
  // Healing tool state
  const [showHealingDialog, setShowHealingDialog] = useState(false);
  const [healedImageUrl, setHealedImageUrl] = useState(null);

  // Display either healed, cropped preview, or original file
  const inputUrl = useMemo(() => {
    if (healedImageUrl) return healedImageUrl;
    if (croppedPreviewUrl) return croppedPreviewUrl;
    if (!files[0]) return null;
    return URL.createObjectURL(files[0]);
  }, [files, croppedPreviewUrl, healedImageUrl]);
  
  // Keep track of original file URL for crop dialog
  useEffect(() => {
    if (files[0]) {
      const url = URL.createObjectURL(files[0]);
      setOriginalFileUrl(url);
      // Reset cropped preview and healed image when new file is uploaded
      setCroppedPreviewUrl(null);
      setHealedImageUrl(null);
      return () => URL.revokeObjectURL(url);
    } else {
      setOriginalFileUrl(null);
      setCroppedPreviewUrl(null);
      setHealedImageUrl(null);
    }
  }, [files]);

  const canProcess = useMemo(() => {
    return files.length === 1 && branchValid && !busy;
  }, [files.length, branchValid, busy]);

  const passportLabel = useMemo(() => {
    if (size === "ca_35x45") return "35×45mm (Canada)";
    return "2×2 inch";
  }, [size]);

  const resetOutputs = () => {
    if (zipUrl) URL.revokeObjectURL(zipUrl);
    if (passportUrl) URL.revokeObjectURL(passportUrl);
    if (sheetUrl) URL.revokeObjectURL(sheetUrl);
    setZipBlob(null);
    setZipUrl(null);
    setPassportUrl(null);
    setSheetUrl(null);
  };
  
  // Handle healing - send image and mask to backend
  const handleHeal = async (maskDataUrl) => {
    if (!files[0] && !healedImageUrl) return;
    
    try {
      const form = new FormData();
      
      // Use healed image if available, otherwise use original file
      if (healedImageUrl) {
        // Convert data URL to blob
        const response = await fetch(healedImageUrl);
        const blob = await response.blob();
        form.append("file", blob, "image.png");
      } else {
        form.append("file", files[0]);
      }
      
      form.append("mask", maskDataUrl);
      form.append("radius", "5");
      form.append("method", "telea");
      
      const res = await api.post("/heal", form, {
        headers: { "Content-Type": "multipart/form-data" },
        responseType: "blob",
        timeout: 60000,
      });
      
      // Create URL for healed image
      const healedUrl = URL.createObjectURL(res.data);
      setHealedImageUrl(healedUrl);
      
      toast({
        title: "Healing applied",
        description: "Marked areas have been healed. You can apply more healing or proceed.",
      });
      
      // Return the new URL for before/after comparison
      return healedUrl;
      
    } catch (e) {
      console.error("Healing failed:", e);
      toast({
        title: "Healing failed",
        description: e?.response?.data?.detail || e?.message || "Could not apply healing",
        variant: "destructive",
      });
      throw e; // Re-throw so HealingTool knows it failed
    }
  };
  
  // Reset healed image
  const resetHealing = () => {
    if (healedImageUrl) {
      URL.revokeObjectURL(healedImageUrl);
      setHealedImageUrl(null);
    }
  };
  
  // Function to generate cropped preview
  const generateCroppedPreview = useCallback(() => {
    if (!files[0] || !croppedAreaPixels) return;
    
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set canvas size to cropped area
      canvas.width = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;
      
      // Draw the cropped portion
      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        croppedAreaPixels.width,
        croppedAreaPixels.height
      );
      
      // Convert to data URL and set as preview
      const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
      setCroppedPreviewUrl(dataUrl);
    };
    image.src = URL.createObjectURL(files[0]);
  }, [files, croppedAreaPixels]);
  
  // Revert to original (uncropped) preview
  const revertToOriginal = () => {
    setCroppedPreviewUrl(null);
    setCroppedAreaPixels(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const validateBranch = async (code) => {
    const v = (code || "").trim();
    if (!v) {
      setBranchValid(false);
      return;
    }

    setValidating(true);
    try {
      const form = new FormData();
      form.append("code", v);
      const res = await api.post(`/branch/validate`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const ok = !!res.data?.valid;
      setBranchValid(ok);
      if (!ok) {
        toast({
          title: "Access denied",
          description: "Branch code is invalid. Ask admin for a valid 8-digit code.",
          variant: "destructive",
        });
      }
    } catch (e) {
      setBranchValid(false);
      toast({
        title: "Validation error",
        description: e?.response?.data?.detail || "Could not validate branch code",
        variant: "destructive",
      });
    } finally {
      setValidating(false);
    }
  };

  useEffect(() => {
    // Allow deep-link like: /passport?branch=12345678
    try {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = (params.get("branch") || "").trim();
      if (fromUrl) {
        setBranchCode(fromUrl);
        validateBranch(fromUrl);
        return;
      }
    } catch {
      // ignore
    }

    if (branchCode) validateBranch(branchCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCropComplete = (_, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  };

  const openCrop = () => {
    if (!files[0]) return;
    setShowCrop(true);
  };

  const run = async () => {
    if (!files[0]) return;
    resetOutputs();

    setBusy(true);
    setProgress(10);
    try {
      const form = new FormData();
      
      // Use healed image if available, otherwise use original file
      if (healedImageUrl) {
        const response = await fetch(healedImageUrl);
        const blob = await response.blob();
        form.append("file", blob, "healed_image.png");
      } else {
        form.append("file", files[0]);
      }
      
      form.append("branch_code", (branchCode || "").trim());
      form.append("size", size);
      form.append("restore", String(restore));
      // Image adjustment parameters
      form.append("brightness", String(brightness));
      form.append("contrast", String(contrast));
      form.append("tint", String(tint));
      form.append("darken", String(darken));
      form.append("remove_shine", String(removeShine));
      form.append("edge_cut", String(edgeCut));  // Edge cut pixels
      form.append("edge_smooth", String(edgeSmooth));  // Edge smooth pixels
      if (useCrop && croppedAreaPixels) {
        form.append("crop", JSON.stringify(croppedAreaPixels));
      }

      // Show upload progress up to 25%, then show "Processing..." at 30%
      const res = await api.post(`/passport-photo`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        responseType: "blob",
        timeout: 300000, // 5 minutes for large photos
        onUploadProgress: (e) => {
          if (!e.total) return;
          const pct = Math.round((e.loaded / e.total) * 25);
          setProgress(Math.min(25, Math.max(10, pct)));
        },
        onDownloadProgress: () => {
          // Once we start getting response, we're at 30%+
          setProgress(prev => Math.max(prev, 30));
        }
      });

      setProgress(60);
      setZipBlob(res.data);
      const url = URL.createObjectURL(res.data);
      setZipUrl(url);
      setProgress(70);

      const zip = await JSZip.loadAsync(res.data);
      const passport =
        zip.file("passport_2x2_300dpi.png") || zip.file("passport_35x45mm_300dpi.png");
      const sheet = zip.file("sheet_4x6_300dpi.png");

      if (passport) {
        const pBlob = await passport.async("blob");
        setPassportUrl(URL.createObjectURL(pBlob));
      }
      setProgress(85);
      
      if (sheet) {
        const sBlob = await sheet.async("blob");
        setSheetUrl(URL.createObjectURL(sBlob));
      }

      setProgress(100);
      toast({
        title: "Ready to print",
        description: "Downloaded files are high-resolution (300 DPI) and arranged on a 4x6 sheet.",
      });
    } catch (e) {
      console.error(e);
      toast({
        title: "Error",
        description: e?.response?.data?.detail || e?.message || "Passport pipeline failed. Please try again.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
      setTimeout(() => setProgress(0), 900);
    }
  };

  return (
    <div className="min-h-screen bg-[#07070a] text-zinc-100" data-testid="passport-studio-page">
      <TopNav />

      <div
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          background:
            "radial-gradient(900px 420px at 20% 0%, rgba(139,92,246,0.22), transparent 60%), radial-gradient(900px 420px at 90% 15%, rgba(14,165,233,0.10), transparent 55%), radial-gradient(900px 420px at 50% 100%, rgba(236,72,153,0.06), transparent 55%)",
        }}
      />

      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <h1
              className="text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl"
              data-testid="passport-title"
            >
              Passport Photo Studio
            </h1>
            <p className="mt-3 text-base text-zinc-300 md:text-lg" data-testid="passport-subtitle">
              Remove background, clean edges, enhance clarity and lighting, then export 2x2 and a 4x6 print sheet.
            </p>

            <div
              className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4"
              data-testid="branch-access-card"
            >
              <div className="flex items-start gap-3">
                <div className={`mt-0.5 grid h-9 w-9 place-items-center rounded-xl ${branchValid ? 'bg-emerald-500/15 ring-1 ring-emerald-300/20' : 'bg-violet-500/15 ring-1 ring-violet-300/20'}`}>
                  <ShieldCheck className={`h-5 w-5 ${branchValid ? 'text-emerald-200' : 'text-violet-200'}`} />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-zinc-100" data-testid="branch-access-title">
                    {branchValid ? "Branch Verified" : "Branch access"}
                  </div>
                  
                  {branchValid ? (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-sm text-emerald-400" data-testid="branch-verified-status">
                        ✓ Access granted
                      </span>
                      <Button
                        onClick={() => {
                          setBranchCode("");
                          setBranchValid(false);
                        }}
                        variant="ghost"
                        size="sm"
                        className="text-xs text-zinc-400 hover:text-zinc-200"
                        data-testid="branch-change-button"
                      >
                        Change
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="mt-1 text-sm text-zinc-400" data-testid="branch-access-subtitle">
                        Enter your 8-digit branch code to unlock processing.
                      </div>

                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <Input
                          value={branchCode}
                          onChange={(e) => {
                            setBranchCode(e.target.value);
                            setBranchValid(false);
                          }}
                          placeholder="e.g. 12345678"
                          className="h-10 w-44 rounded-2xl bg-black/20 ring-1 ring-white/10"
                          data-testid="branch-code-input"
                        />
                        <Button
                          onClick={() => validateBranch(branchCode)}
                          disabled={validating}
                          className="rounded-full bg-violet-500 text-white hover:bg-violet-400"
                          data-testid="branch-validate-button"
                        >
                          {validating ? "Checking…" : "Validate"}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {progress > 0 ? (
              <div className="mt-6 space-y-2" data-testid="passport-progress-wrapper">
                <div className="flex items-center justify-between text-xs text-zinc-400">
                  <span data-testid="passport-progress-label">
                    {progress <= 25 ? "Uploading..." : progress < 60 ? "Removing background..." : progress < 85 ? "Creating photos..." : "Finalizing..."}
                  </span>
                  <span data-testid="passport-progress-value">{progress}%</span>
                </div>
                <Progress value={progress} data-testid="passport-progress-bar" />
              </div>
            ) : null}

            <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="passport-actions">
              <Button
                onClick={run}
                disabled={!canProcess}
                className="rounded-full bg-violet-500 text-white hover:bg-violet-400 disabled:opacity-50"
                data-testid="passport-run-button"
              >
                Create print-ready pack
              </Button>

              <Button
                onClick={() => zipUrl && downloadBlob(zipUrl, "passport-print-ready.zip")}
                disabled={!zipUrl}
                variant="secondary"
                className="rounded-full bg-white/5 text-zinc-100 ring-1 ring-white/10 hover:bg-white/10 disabled:opacity-50"
                data-testid="passport-download-zip-button"
              >
                <Download className="mr-2 h-4 w-4" />
                Download ZIP
              </Button>
            </div>

            <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="passport-options">
              <div className="grid gap-4 md:grid-cols-2" data-testid="passport-size-restore-row">
                <div data-testid="passport-size-wrapper">
                  <div className="text-sm font-medium text-zinc-100" data-testid="passport-size-label">
                    Photo size
                  </div>
                  <div className="mt-2">
                    <Select value={size} onValueChange={setSize} data-testid="passport-size-select">
                      <SelectTrigger
                        className="h-10 rounded-2xl bg-black/20 ring-1 ring-white/10"
                        data-testid="passport-size-trigger"
                      >
                        <SelectValue placeholder="Choose size" data-testid="passport-size-value" />
                      </SelectTrigger>
                      <SelectContent className="rounded-2xl" data-testid="passport-size-content">
                        <SelectItem value="us_2x2" data-testid="passport-size-us">
                          2×2 inch (US)
                        </SelectItem>
                        <SelectItem value="ca_35x45" data-testid="passport-size-ca">
                          35×45mm (Canada)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="mt-2 text-xs text-zinc-500" data-testid="passport-size-hint">
                    Output includes your selected size + a 4×6 sheet with 6 copies.
                  </div>
                </div>

                <div data-testid="passport-restore-wrapper">
                  <div className="text-sm font-medium text-zinc-100" data-testid="passport-restore-label">
                    Auto Restoration
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-3 rounded-2xl bg-black/20 px-3 py-2 ring-1 ring-white/10">
                    <div>
                      <div className="text-sm text-zinc-200" data-testid="passport-restore-title">
                        Repair faded / blurry photos
                      </div>
                      <div className="text-xs text-zinc-500" data-testid="passport-restore-subtitle">
                        Denoise + sharpen + lighting + tint correction
                      </div>
                    </div>
                    <Switch checked={restore} onCheckedChange={setRestore} data-testid="passport-restore-switch" />
                  </div>
                </div>

                {/* Image Adjustment Controls */}
                <div className="space-y-4 rounded-2xl bg-black/20 p-4 ring-1 ring-white/10" data-testid="adjustment-controls">
                  <div className="text-sm font-medium text-zinc-100">Manual Adjustments</div>
                  
                  {/* Brightness */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-zinc-300">
                        <Sun className="h-4 w-4" />
                        Brightness
                      </div>
                      <span className="text-xs text-zinc-500">{brightness}</span>
                    </div>
                    <Slider
                      value={[brightness]}
                      onValueChange={([v]) => setBrightness(v)}
                      min={-100}
                      max={100}
                      step={1}
                      className="w-full"
                      data-testid="brightness-slider"
                    />
                  </div>
                  
                  {/* Contrast */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-zinc-300">
                        <Contrast className="h-4 w-4" />
                        Contrast
                      </div>
                      <span className="text-xs text-zinc-500">{contrast}</span>
                    </div>
                    <Slider
                      value={[contrast]}
                      onValueChange={([v]) => setContrast(v)}
                      min={-100}
                      max={100}
                      step={1}
                      className="w-full"
                      data-testid="contrast-slider"
                    />
                  </div>
                  
                  {/* Tint (Color Temperature) */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-zinc-300">
                        <Palette className="h-4 w-4" />
                        Tint
                      </div>
                      <span className="text-xs text-zinc-500">{tint > 0 ? `Warm +${tint}` : tint < 0 ? `Cool ${tint}` : "0"}</span>
                    </div>
                    <Slider
                      value={[tint]}
                      onValueChange={([v]) => setTint(v)}
                      min={-100}
                      max={100}
                      step={1}
                      className="w-full"
                      data-testid="tint-slider"
                    />
                  </div>
                  
                  {/* Darken */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-zinc-300">
                        <Moon className="h-4 w-4" />
                        Darken Highlights
                      </div>
                      <span className="text-xs text-zinc-500">{darken}</span>
                    </div>
                    <Slider
                      value={[darken]}
                      onValueChange={([v]) => setDarken(v)}
                      min={0}
                      max={100}
                      step={1}
                      className="w-full"
                      data-testid="darken-slider"
                    />
                  </div>
                  
                  {/* Edge Cut - Remove halo/ring around edges */}
                  <div className="space-y-2 pt-2 border-t border-white/10">
                    <div className="flex items-center gap-2">
                      <Scissors className="h-4 w-4 text-zinc-400" />
                      <span className="text-sm text-zinc-200">Edge Cut</span>
                      <span className="ml-auto text-xs text-zinc-500">{edgeCut}px</span>
                    </div>
                    <Slider
                      value={[edgeCut]}
                      onValueChange={([v]) => setEdgeCut(v)}
                      min={0}
                      max={50}
                      step={1}
                      className="w-full"
                      data-testid="edge-cut-slider"
                    />
                    <div className="text-xs text-zinc-500">
                      Removes halo/ring around edges. Higher = more cut.
                    </div>
                  </div>
                  
                  {/* Smooth Edge - Soften the edges */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-zinc-400" />
                      <span className="text-sm text-zinc-200">Smooth Edge</span>
                      <span className="ml-auto text-xs text-zinc-500">{edgeSmooth}px</span>
                    </div>
                    <Slider
                      value={[edgeSmooth]}
                      onValueChange={([v]) => setEdgeSmooth(v)}
                      min={0}
                      max={20}
                      step={1}
                      className="w-full"
                      data-testid="edge-smooth-slider"
                    />
                    <div className="text-xs text-zinc-500">
                      Softens hard edges. 0 = sharp, 20 = very soft.
                    </div>
                  </div>
                  
                  {/* Remove Shine Toggle - Feature disabled
                  <div className="flex items-center justify-between pt-2 border-t border-white/10">
                    <div>
                      <div className="text-sm text-zinc-200">Remove Face Shine</div>
                      <div className="text-xs text-zinc-500">Auto-removes glare on nose & forehead</div>
                    </div>
                    <Switch checked={removeShine} onCheckedChange={setRemoveShine} data-testid="remove-shine-switch" />
                  </div>
                  */}
                  
                  {/* Reset Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setBrightness(0);
                      setContrast(0);
                      setTint(0);
                      setDarken(0);
                      setEdgeCut(18);  // Reset to default
                      setEdgeSmooth(0);  // Reset to default
                    }}
                    className="w-full text-xs text-zinc-400 hover:text-zinc-200"
                    data-testid="reset-adjustments-btn"
                  >
                    Reset All Adjustments
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3" data-testid="crop-toggle-row">
                <div>
                  <div className="text-sm font-medium text-zinc-100" data-testid="crop-toggle-title">
                    Manual crop (recommended)
                  </div>
                  <div className="mt-1 text-sm text-zinc-400" data-testid="crop-toggle-subtitle">
                    Adjust head position and framing before generating 2x2.
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={useCrop}
                    onCheckedChange={setUseCrop}
                    data-testid="crop-switch"
                  />
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2" data-testid="crop-actions">
                <Button
                  onClick={openCrop}
                  disabled={!files[0]}
                  variant="secondary"
                  className="rounded-full bg-white/5 text-zinc-100 ring-1 ring-white/10 hover:bg-white/10"
                  data-testid="open-cropper-button"
                >
                  <Scissors className="mr-2 h-4 w-4" />
                  Adjust crop
                </Button>
                
                <Button
                  onClick={() => setShowHealingDialog(true)}
                  disabled={!files[0]}
                  variant="secondary"
                  className="rounded-full bg-white/5 text-zinc-100 ring-1 ring-white/10 hover:bg-white/10"
                  data-testid="open-healing-button"
                >
                  <Wand2 className="mr-2 h-4 w-4" />
                  Healing Tool
                </Button>
                
                <div className="text-xs text-zinc-500" data-testid="crop-hint">
                  If you skip crop, we auto center-crop.
                </div>
              </div>
              
              {/* Healed indicator */}
              {healedImageUrl && (
                <div className="mt-3 flex items-center justify-between rounded-xl bg-emerald-500/10 px-3 py-2 ring-1 ring-emerald-500/20">
                  <span className="text-sm text-emerald-400">Healing applied</span>
                  <Button
                    onClick={resetHealing}
                    variant="ghost"
                    size="sm"
                    className="text-xs text-zinc-400 hover:text-zinc-200 h-6 px-2"
                    data-testid="reset-healing-button"
                  >
                    Reset
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-7">
            <Card className="rounded-3xl border-white/10 bg-zinc-950/50" data-testid="passport-upload-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base md:text-lg" data-testid="passport-upload-title">
                  Upload
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <Dropzone mode="single" files={files} setFiles={setFiles} maxFiles={1} />

                <div className="grid gap-4 lg:grid-cols-2" data-testid="passport-previews">
                  <div className="space-y-2" data-testid="passport-input-preview">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-zinc-200" data-testid="passport-input-title">
                        Input 
                        {healedImageUrl && <span className="text-xs text-emerald-400 ml-2">(Healed)</span>}
                        {croppedPreviewUrl && !healedImageUrl && <span className="text-xs text-emerald-400 ml-2">(Cropped)</span>}
                      </div>
                      {(croppedPreviewUrl || healedImageUrl) && (
                        <Button
                          onClick={() => {
                            revertToOriginal();
                            resetHealing();
                          }}
                          variant="ghost"
                          size="sm"
                          className="text-xs text-zinc-400 hover:text-zinc-200 h-6 px-2"
                          data-testid="revert-crop-button"
                        >
                          Revert to Original
                        </Button>
                      )}
                    </div>
                    <div className="overflow-hidden rounded-2xl border border-white/10 bg-zinc-950/40">
                      <div className="h-64">
                        {inputUrl ? (
                          <img
                            src={inputUrl}
                            alt="input"
                            className="h-full w-full object-contain"
                            data-testid="passport-input-image"
                          />
                        ) : (
                          <div className="grid h-full place-items-center text-sm text-zinc-400" data-testid="passport-input-empty">
                            Choose a file to preview
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2" data-testid="passport-output-preview">
                    <div className="text-sm font-medium text-zinc-200" data-testid="passport-output-title">
                      {busy ? "Processing..." : "Output preview"}
                    </div>
                    <div className="overflow-hidden rounded-2xl border border-white/10 bg-zinc-950/40">
                      <div 
                        className="h-64 flex items-center justify-center"
                        style={{
                          backgroundImage: "linear-gradient(45deg, #1a1a1a 25%, transparent 25%), linear-gradient(-45deg, #1a1a1a 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #1a1a1a 75%), linear-gradient(-45deg, transparent 75%, #1a1a1a 75%)",
                          backgroundSize: "20px 20px",
                          backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px",
                        }}
                      >
                        {busy ? (
                          <div className="text-center space-y-3">
                            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-violet-500 mx-auto"></div>
                            <div className="text-sm text-zinc-400">
                              {progress <= 25 ? "Uploading..." : progress < 60 ? "Removing background..." : progress < 85 ? "Creating passport photos..." : "Finalizing..."}
                            </div>
                            <div className="text-xs text-zinc-500">{progress}%</div>
                          </div>
                        ) : passportUrl ? (
                          <img
                            src={passportUrl}
                            alt="output preview"
                            className="h-full w-full object-contain"
                            data-testid="passport-output-image"
                          />
                        ) : (
                          <div className="text-sm text-zinc-400" data-testid="passport-output-empty">
                            Result will appear here
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-zinc-500" data-testid="passport-output-note">
                      Final outputs are on white background and delivered as print-ready PNGs.
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 lg:grid-cols-2" data-testid="passport-final-previews">
                  <div className="space-y-2" data-testid="preview-2x2">
                    <div className="text-sm font-medium text-zinc-200" data-testid="preview-2x2-title">
                      {passportLabel} (300 DPI)
                    </div>
                    <div className="overflow-hidden rounded-2xl border border-white/10 bg-zinc-950/40">
                      <div className="h-64">
                        {passportUrl ? (
                          <img
                            src={passportUrl}
                            alt="passport"
                            className="h-full w-full object-contain"
                            data-testid="preview-2x2-image"
                          />
                        ) : (
                          <div className="grid h-full place-items-center text-sm text-zinc-400" data-testid="preview-2x2-empty">
                            Generated 2x2 will appear here
                          </div>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() =>
                        passportUrl &&
                        downloadBlob(
                          passportUrl,
                          size === "ca_35x45" ? "passport_35x45mm_300dpi.png" : "passport_2x2_300dpi.png",
                        )
                      }
                      disabled={!passportUrl}
                      variant="secondary"
                      className="w-full rounded-2xl bg-white/5 text-zinc-100 ring-1 ring-white/10 hover:bg-white/10 disabled:opacity-50"
                      data-testid="download-2x2-button"
                    >
                      Download 2x2 PNG
                    </Button>
                  </div>

                  <div className="space-y-2" data-testid="preview-4x6">
                    <div className="text-sm font-medium text-zinc-200" data-testid="preview-4x6-title">
                      4x6 sheet (300 DPI)
                    </div>
                    <div className="overflow-hidden rounded-2xl border border-white/10 bg-zinc-950/40">
                      <div className="h-64">
                        {sheetUrl ? (
                          <img
                            src={sheetUrl}
                            alt="4x6 sheet"
                            className="h-full w-full object-contain"
                            data-testid="preview-4x6-image"
                          />
                        ) : (
                          <div className="grid h-full place-items-center text-sm text-zinc-400" data-testid="preview-4x6-empty">
                            Generated sheet will appear here
                          </div>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() => sheetUrl && downloadBlob(sheetUrl, "sheet_4x6_300dpi.png")}
                      disabled={!sheetUrl}
                      variant="secondary"
                      className="w-full rounded-2xl bg-white/5 text-zinc-100 ring-1 ring-white/10 hover:bg-white/10 disabled:opacity-50"
                      data-testid="download-4x6-button"
                    >
                      Download 4x6 PNG
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Dialog open={showCrop} onOpenChange={setShowCrop}>
        <DialogContent className="max-w-3xl rounded-3xl border-white/10 bg-zinc-950/90" data-testid="crop-dialog">
          <DialogHeader>
            <DialogTitle className="text-base md:text-lg" data-testid="crop-dialog-title">
              Adjust crop (square)
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4" data-testid="crop-dialog-body">
            <div className="relative h-[420px] overflow-hidden rounded-2xl border border-white/10 bg-black/40" data-testid="cropper-stage">
              {originalFileUrl ? (
                <Cropper
                  image={originalFileUrl}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              ) : null}
            </div>

            <div className="grid gap-3 md:grid-cols-2" data-testid="crop-controls">
              <div>
                <div className="text-sm text-zinc-300" data-testid="crop-zoom-label">
                  Zoom
                </div>
                <Input
                  type="range"
                  min={1}
                  max={3}
                  step={0.01}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="mt-2"
                  data-testid="crop-zoom-range"
                />
              </div>
              <div>
                <div className="text-sm text-zinc-300" data-testid="crop-rect-label">
                  Crop rectangle (pixels)
                </div>
                <div className="mt-2 rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-xs text-zinc-300" data-testid="crop-rect-value">
                  {croppedAreaPixels
                    ? `${Math.round(croppedAreaPixels.width)}×${Math.round(croppedAreaPixels.height)} @ (${Math.round(croppedAreaPixels.x)}, ${Math.round(croppedAreaPixels.y)})`
                    : "Move the crop to set a rectangle"}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2" data-testid="crop-dialog-actions">
              <Button
                variant="secondary"
                onClick={() => setShowCrop(false)}
                className="rounded-full bg-white/5 text-zinc-100 ring-1 ring-white/10 hover:bg-white/10"
                data-testid="crop-cancel-button"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  generateCroppedPreview();
                  setShowCrop(false);
                }}
                className="rounded-full bg-violet-500 text-white hover:bg-violet-400"
                data-testid="crop-apply-button"
              >
                Apply Crop
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Healing Tool Dialog */}
      <Dialog open={showHealingDialog} onOpenChange={setShowHealingDialog}>
        <DialogContent className="max-w-2xl rounded-3xl border-white/10 bg-zinc-950/90" data-testid="healing-dialog">
          <DialogHeader>
            <DialogTitle className="text-base md:text-lg" data-testid="healing-dialog-title">
              Healing Tool
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4" data-testid="healing-dialog-body">
            <HealingTool 
              imageUrl={healedImageUrl || (files[0] ? URL.createObjectURL(files[0]) : null)}
              onHeal={handleHeal}
              disabled={busy}
            />
            
            <div className="flex justify-end">
              <Button
                onClick={() => setShowHealingDialog(false)}
                className="rounded-full bg-violet-500 text-white hover:bg-violet-400"
                data-testid="healing-done-button"
              >
                Done
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
